import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Gift, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await authLogin({ login, password });
      toast.success(`Bienvenue, ${user.prenom} !`);
      
      // Redirect based on role
      const mobileRoles = ['remettant_magasin', 'remettant_point_retrait'];
      if (mobileRoles.includes(user.role)) {
        navigate('/mobile');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Background image section */}
      <div 
        className="hidden lg:block fixed inset-0 w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1768796372882-8b67936af681?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjB3b3JrZXIlMjB0YWJsZXR8ZW58MHx8fHwxNzczMjQ3NjA4fDA&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-indigo-900/70" />
        <div className="relative h-full flex flex-col justify-center items-center text-white p-12">
          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
            <Gift className="h-8 w-8" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-center">KADOLOGISTIQUE</h1>
          <p className="text-lg text-white/80 text-center max-w-md">
            Système de gestion et de suivi des remises de lots
          </p>
        </div>
      </div>

      {/* Login form section */}
      <div className="flex-1 flex items-center justify-center lg:ml-[50%] p-4 sm:p-6 lg:p-12">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="space-y-2 pb-6">
            <div className="lg:hidden flex justify-center mb-4">
              <div className="w-14 h-14 bg-indigo-700 rounded-2xl flex items-center justify-center">
                <Gift className="h-7 w-7 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center lg:text-left">
              <span className="lg:hidden">KADOLOGISTIQUE</span>
              <span className="hidden lg:inline">Connexion</span>
            </CardTitle>
            <CardDescription className="text-center lg:text-left">
              Connectez-vous à votre espace de travail
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-fade-in">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="login">Identifiant</Label>
                <Input
                  id="login"
                  type="text"
                  placeholder="Entrez votre identifiant"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="login-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Entrez votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="password-input"
                  className="h-11"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-11 bg-indigo-700 hover:bg-indigo-800"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
